import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import { sortBy } from "parog-web/utils/sort";
import tWithDefault from "parog-web/utils/t-with-default";
import type ApicryptMessageReceivedJournalEvent from "./apicrypt-message-received-journal-event";
import type BioExamResult from "./bio-exam-result";
import type BioExamResultsApprovedJournalEvent from "./bio-exam-results-approved-journal-event";
import type FileAttachment from "./file-attachment";
import type { Summary } from "./journal-event";

//---- Automatically generated, don't update this part ----

type OruStatusStringEnum = "F" | "P" | "M" | "I" | "R" | "C" | "O" | "D" | "X";

type MatchingPageTypeStringEnum = "medics_web" | "matchlab";

export default class BioExamResultsReceivedJournalEvent extends JournalEvent {
  static type = "bio-exam-results-received-journal-event";

  @attr("string") declare rawOru: string;

  @attr("string") declare oruId: string;

  @attr("string") declare oruStatus: OruStatusStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare isFromHprim: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare isFromOru: boolean;

  /**
   * Whether the bio exam has been processed or not.
   * Will be used only for bio exam results which come from Apicrypt.
   */
  @attr("boolean", {
    allowNull: true
  })
  declare isDraft: boolean;

  /**
   * HLA result ID
   */
  @attr("string") declare hlaId: string;

  /**
   * When the HLA consultation report has been edited
   */
  @attr("date") declare hlaCrDate: Date;

  @attr("string") declare matchingPageType: MatchingPageTypeStringEnum;

  @attr("string") declare lab: string;

  @attr("boolean", {
    allowNull: true
  })
  declare normalized: boolean;

  @hasMany("bio-exam-results-approved-journal-event", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvent"
  })
  declare bioExamResultsApprovedJournalEvents: BioExamResultsApprovedJournalEvent[];

  static validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.bio-exam-results-received-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      oruStatus: validateInclusion({
        list: ["F", "P", "M", "I", "R", "C", "O", "D", "X"],
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-results-received-journal-event.oru-status"
        )
      }),
      matchingPageType: validateInclusion({
        list: ["medics_web", "matchlab"],
        allowBlank: true,
        description: intl.t(
          "validations.bio-exam-results-received-journal-event.matching-page-type"
        )
      })
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvents"
  })
  declare patient: Patient;

  /**
   * A list of Bio Exam Results, represented using an array of object.
   * Each object of the array is a structured object of type BioExamResult :
   * {
   *   code: "LOINC-630-4",
   *   value: "10",
   *   label: "ECBU",
   *   unit: "g/L"
   * }
   */
  @attr("bio-exam-result", {
    defaultValue() {
      return [];
    }
  })
  declare results: BioExamResult[];

  @hasMany("apicrypt-message-received-journal-event", {
    async: false,
    inverse: "bioExamResultsReceivedJournalEvent"
  })
  declare apicryptMessageReceivedJournalEvents: ApicryptMessageReceivedJournalEvent[];

  get lastApicryptMessage(): ApicryptMessageReceivedJournalEvent {
    return this.apicryptMessageReceivedJournalEvents
      .toSorted(sortBy("createdAt"))
      .at(-1) as ApicryptMessageReceivedJournalEvent;
  }

  @hasMany("file-attachment", {
    async: false,
    inverse: null
  })
  declare fileAttachmentsWithoutZip: FileAttachment[];

  get orderedFileAttachments(): FileAttachment[] {
    return this.fileAttachmentsWithoutZip.toSorted(sortBy("index"));
  }

  /**
   * Find the BioExamResult with the given code
   */
  result(code: string): BioExamResult | undefined {
    return this.results.find(r => r.code === code);
  }

  hasAlertForCode(code: string): boolean {
    if (!code || !this.isAlertingJournalEvent) return false;

    const normalizedCode = code.replace("LOINC-", "");
    return Boolean(
      this.alertTriggeredJournalEvents.find(alert => {
        return alert.code == normalizedCode;
      })
    );
  }

  get summary(): Summary {
    return Object.assign({}, super.summary, {
      forcIdentifier: {
        feature: "bio-results",
        component: "edition"
      }
    });
  }

  get showCorrection(): boolean {
    return this.oruStatus === "C" && this.validationStatus == "isDraft";
  }

  get isFromExternalSource(): boolean {
    return this.isFromHprim || this.isFromOru || Boolean(this.hlaId);
  }

  get sourceFileType(): string | undefined {
    if (this.isFromHprim) return "hprim";
    if (this.isFromOru) return "oru";

    return;
  }

  get validationStatus(): "isDraft" | "isValidated" | "isPassivelyValidated" {
    // if patient is not TLS (meaning he's "data only")
    if (!this.patient.tlsAgreed) {
      return "isPassivelyValidated";
    }

    // if patient is TLS but the JE was created before he accepted the TLS
    if (this.patient.tlsAgreedAt && this.patient.tlsAgreedAt > this.createdAt) {
      return "isPassivelyValidated";
    }

    return this.isDraft ? "isDraft" : "isValidated";
  }

  async approve(): Promise<void> {
    this.isDraft = false;
    const results = this.results.map(res => {
      res.isDraft = false;
      return res;
    });
    this.results = results;
    this.save();

    await this.store
      .createRecord("bio-exam-results-approved-journal-event", {
        bioExamResultsReceivedJournalEvent: this,
        patient: this.patient,
        occurredAt: new Date()
      })
      .save();
  }

  get canEdit(): boolean {
    return !this.hlaId;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "bio-exam-results-received-journal-event": BioExamResultsReceivedJournalEvent;
  }
}
